import  { useMemo, useState, useCallback, useEffect } from "react";
import { changeMode, changeModeForField, fieldTruePermission, prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import useColumn from "../../../../utils/customHooks/useColumns";
import usePageState from "../../../../utils/customHooks/usePageState";
import { useForm } from "react-hook-form";
import { STAUTES } from "../../../../utils/constant";
import { deleteApi, postApi, putApi } from "../../../../utils/services";
import {
  ADD_RAP_WPS_ACTIVITIES,
  DELETE_RAP_WPS_ACTIVITIES,
  GET_ALL_WPS_ACTIVITIES,
  UPDATE_RAP_WPS_ACTIVITIES
} from "../../../../utils/services/apiPath";
import { useParams } from "react-router-dom";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { ProTextInput, ProGrid, NameCell, DeleteCell, TabGridAndFormLayout } from "@proscore-ai/proscore-ui-library";
import useCsvExport from "../../../../utils/useCsvExporter";

const WPSActivitiesFormConfing = {
  wpsTitle: {
    label: "WPS Title",
    rules: { required: "WPS Title is required!" }
  },
  wpsDescription: {
    label: "WPS Description",
    fieldProps: { multiline: true, rows: 4 },
    rules: { required: "WPS Description is required!" }
  },
  requiredHours: {
    label: "Required Hours",
    rules: { required: "Required Hours is required!" }
  }
};
export default function WpsActivities() {
  const { id } = useParams();
  const [showGrid, setShowGrid] = useState(true);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(WPSActivitiesFormConfing));
  const [wpsActivitiesData, setWpsActivitiesData] = useState({});
  const [actionData, setActionData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const {
    currentPage,
    pageSize,
    data: wpsData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const columns = useColumn(
    [
      {
        field: "wpsTitle",
        headerName: "Title",
        renderCell: params => <NameCell params={params} onClick={onUserClick} />
      },
      {
        field: "wpsDescription",
        headerName: "Description"
      },
      {
        field: "requiredHours",
        headerName: "Required Hours"
      },
      {
        field: "wpsActivitiesGuild",
        headerName: "Actions",
        isDeleteColumn: true,
        disableExport: true,
        width: 80,
        renderCell: params => <DeleteCell title={"Delete WPS Activities"} params={params} onDelete={onDelete} />
      }
    ],
    true
  );
  const { handleSubmit, control, setValue } = useForm({ defaultValues: prepareDefaultValues(WPSActivitiesFormConfing) });
  const isEditModeOn = useMemo(() => Object.values(formConfig).some(({ mode }) => mode === "edit"), [formConfig]);

  const onEdit = () => setFormConfig(changeMode("edit"));
  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const resetForm = () => {
    setActionData({});
    setShowGrid(true);
    setFormConfig(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: { ...prev[key], mode: "read" } }), {}));
  };

  const onCancel = () => {
    resetForm();
    setShowGrid(true);
  };

  const onUserClick = params => {
    setShowGrid(false);
    setActionData(params.row);
    getClientUserData(params.row);
  };

  const addButtonAction = () => {
    Object.keys(formConfig).forEach(field => setValue(field, null));
    onEdit();
    setShowGrid(false);
  };

  const onSave = async data => {
    if (isSaving) return; 
    setIsSaving(true);
    const payload = {
      wpsTitle: data?.wpsTitle,
      wpsDescription: data?.wpsDescription,
      requiredHours: parseInt(data?.requiredHours),
      rapInfoGuid: id,
      rapwpsActivityGuid: actionData?.rapwpsActivityGuid ?? null
    };
    const methodApi = actionData?.rapwpsActivityGuid ? putApi : postApi;
    const { error } = await methodApi(actionData?.rapwpsActivityGuid ? UPDATE_RAP_WPS_ACTIVITIES : ADD_RAP_WPS_ACTIVITIES, payload);
    if (error) return errorToast(error);
    getAllWPSActivities();
    resetForm();
    setIsSaving(false);
    successToast(`WPS Activities ${actionData?.rapwpsActivityGuid ? "updated" : "added"} successfully.`);
  };
  const onDelete = async id => {
    const { error } = await deleteApi(`${DELETE_RAP_WPS_ACTIVITIES}?rapWpsActivityId=${id}`);
    if (error) return errorToast(error);
    getAllWPSActivities();
    setShowGrid(true);
    successToast("WPS Activities deleted successfully.");
  };

  const getClientUserData = updateData => {
    const data = {
      wpsTitle: updateData?.wpsTitle,
      wpsDescription: updateData?.wpsDescription,
      requiredHours: updateData?.requiredHours
    };
    setWpsActivitiesData(data);
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
  };

  const getAllWPSActivities = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      ...sortModel
    };
    const { data, totalRecords, error } = await postApi(GET_ALL_WPS_ACTIVITIES, payload, { params: { rapInfoId: id } });
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const handleDownloadCSV = useCsvExport({
    api: GET_ALL_WPS_ACTIVITIES,

    getPayload: () => {
      if (!id) {
        errorToast("RAP Info not found for export.");
        return null;
      }

      return {
        pageIndex: 1,
        pageSize: rowCount || 10000,
        searchString,
        orderByAscending: true,
        ...sortModel
      };
    },

    getParams: () => ({
      params: { rapInfoId: id }
    }),

    mapData: data =>
      data.map(item => ({
        "Title": item.wpsTitle || "",
        "Description": item.wpsDescription || "",
        "Required Hours": item.requiredHours || ""
      })),

    fileName: "WpsActivities.csv"
  });

  useEffect(() => {
    getAllWPSActivities();
  }, [getAllWPSActivities]);

  const defaultFormProps = { control, formValues: wpsActivitiesData, handleReadValueClick };
  return (
    <TabGridAndFormLayout
      showGrid={showGrid}
      title={`${actionData?.rapwpsActivityGuid ? "Edit" : "Add"} WPS Activities`}
      backAction={() => resetForm()}
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      backLabel={"Go Back to WPS Activities"}
      canView={true}
      hideEdit={false}
      onSave={handleSubmit(onSave)}
      onCancel={onCancel}
      isSaving={isSaving}
    >
      {showGrid && (
        <ProGrid
          title={`WPS Activities`}
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.rapwpsActivityGuid,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          columns={columns}
          rows={wpsData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          addButtonLabel="+ Add WPSActivity"
          hideExport={false}
          hideAddButton={false}
          addButtonAction={addButtonAction}
          exportAction={handleDownloadCSV}
        />
      )}
      {!showGrid && (
        <>
          <ProTextInput {...defaultFormProps} {...formConfig.wpsTitle} permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.requiredHours} type="number" permission={fieldTruePermission} />
          <ProTextInput {...defaultFormProps} {...formConfig.wpsDescription} permission={fieldTruePermission} />
        </>
      )}
    </TabGridAndFormLayout>
  );
}
